import { getActiveAppIdForHttpHeader } from './activeAppIdHeader';
import { normalizeIdentityPayload } from './identityFromApi';
import {
    clearAuthCache,
    getAccessToken,
    setAccessToken,
    setIdentityCached,
} from './tokenStorage';
import { API_BASE_URL } from './apiBaseUrl';

import { HttpHeaderNames } from './httpHeaders';

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                clearAuthCache();
                throw new Error('Refresh failed');
            }
            const data = (await res.json()) as {
                accessToken: string;
                identity?: unknown;
                Identity?: unknown;
            };
            if (!data?.accessToken) {
                clearAuthCache();
                throw new Error('Invalid refresh response');
            }
            setAccessToken(data.accessToken);
            const identity = normalizeIdentityPayload(data.identity ?? data.Identity);
            if (identity) setIdentityCached(identity);
            return data.accessToken;
        })().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

// Reference/lookup endpoints implicitly needed by transaction forms (bankPayments, cashPayments, etc.).
// Users may have document access without direct menu access to these master screens — the backend
// should bundle these read permissions with document access. Until it does, fail silently: the
// dropdowns will be empty but no confusing "Access denied" toast should appear.
const IMPLICIT_REFERENCE_PREFIXES = [
    '/api/glVoucherTypes',
    '/api/glChartAccounts',
    '/api/glAccountGroups',
];

function isImplicitReferencePath(path: string): boolean {
    const lower = path.toLowerCase();
    return IMPLICIT_REFERENCE_PREFIXES.some(p => lower.startsWith(p.toLowerCase()));
}

function dispatch403(res: Response, path: string) {
    if (res.status !== 403) return;
    void (async () => {
        try {
            const t = await res.clone().text();
            const j = t ? JSON.parse(t) : {};
            if (j && typeof j === 'object' && j.code === 'company_license_expired') {
                window.dispatchEvent(new CustomEvent('fbr-license-changed'));
                return;
            }
            if (isImplicitReferencePath(path)) return;
            const code = (typeof j?.code === 'string' && j.code) ? j.code : 'access_denied';
            const msg = (typeof j?.message === 'string' && j.message) ? j.message : 'Access denied';
            window.dispatchEvent(new CustomEvent('fbr-access-denied', { detail: { code, message: msg } }));
        } catch {
            if (!isImplicitReferencePath(path)) {
                window.dispatchEvent(
                    new CustomEvent('fbr-access-denied', { detail: { code: 'access_denied', message: 'Access denied' } })
                );
            }
        }
    })();
}

// ── Phase 3 option (a), A6 behaviour: an unrecognised X-Permission-Version means an admin
// changed this user's grants mid-session. Silently refetch /me and rewrite the identity cache
// (which fires 'fbr-identity-changed' so menus/toolbars re-render). NEVER a logout, NEVER a
// reload — an admin editing grants must not read as an incident to the users it touches.
let lastPermissionVersion: string | null = null;
let permissionRefetchInFlight = false;

function watchPermissionVersion(res: Response): void {
    const v = res.headers.get('X-Permission-Version');
    if (!v) return;
    if (lastPermissionVersion === null) {
        lastPermissionVersion = v;
        return;
    }
    if (v === lastPermissionVersion || permissionRefetchInFlight) return;
    lastPermissionVersion = v;
    permissionRefetchInFlight = true;
    void (async () => {
        try {
            const me = await fetch(`${API_BASE_URL}/api/auth/me`, {
                method: 'GET',
                credentials: 'include',
                headers: { Accept: 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
            });
            if (me.ok) {
                const identity = normalizeIdentityPayload(await me.json());
                if (identity) setIdentityCached(identity);
            }
        } catch {
            // Background refresh only — the next explicit auth interaction recovers.
        } finally {
            permissionRefetchInFlight = false;
        }
    })();
}

export async function apiFetch(
    path: string,
    init: RequestInit = {},
    opts: { auth?: boolean; retryOn401?: boolean } = {}
) {
    const auth = opts.auth ?? true;
    const retryOn401 = opts.retryOn401 ?? true;

    const headers = new Headers(init.headers ?? {});
    headers.set('Accept', 'application/json');

    if (auth) {
        const token = getAccessToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
            headers.set(HttpHeaderNames.ActiveAppId, getActiveAppIdForHttpHeader());
        }
    }

    const res = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers,
        credentials: 'include',
    });

    if (auth) dispatch403(res, path);
    if (auth) watchPermissionVersion(res);

    if (res.status !== 401 || !auth || !retryOn401) return res;

    // Try refresh once, then retry original request
    try {
        const newToken = await refreshAccessToken();
        const retryHeaders = new Headers(init.headers ?? {});
        retryHeaders.set('Accept', 'application/json');
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        retryHeaders.set(HttpHeaderNames.ActiveAppId, getActiveAppIdForHttpHeader());

        const retryRes = await fetch(`${API_BASE_URL}${path}`, {
            ...init,
            headers: retryHeaders,
            credentials: 'include',
        });
        if (auth) dispatch403(retryRes, path);
        return retryRes;
    } catch {
        return res;
    }
}
